import axios from 'axios';
import React, { FC, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import {
	IShipmentService,
	Shipment,
	ShipmentDirection,
	ShipmentTrackingEvent,
} from '../../../../@posturinn/client-react';
import { IAuthService } from '../../../../@posturinn/common-react/core';
import { setActiveModal } from '../../../../actions/modalActions';
import { ATTACH_FILES_MODAL } from '../../../../constants/documentTypes/modals/modals';
import theme from '../../../../constants/theme';
import { getCurrentLangPretty } from '../../../../helpers/url/urlHelpers';
import useShipmentDetails from '../../../../hooks/useShipmentDetails';
import useShipmentFees from '../../../../hooks/useShipmentFees';
import { IShipmentDetails, IShipmentFees } from '../../../../interfaces/reducers/shipmentsReducer';
import Button from '../../../base/button/button';
import { H4, H5, P } from '../../../base/typography';
import ChronologicalOrderedEvents from '../../../chronologicalOrderedEvents';
import { RawLoader } from '../../../loading/loading';
import * as StyledShipmentDetails from './shipmentDetails.styles';

//import { ReactComponent as IconFile } from '../../../../icons/file.svg';

interface Props {
	shipment: Shipment;
	delivered: boolean;
	page: number;
	dropOff?: boolean;
}

const ShipmentDetails: FC<Props> = ({ shipment, delivered, page, dropOff }: Props) => {
	const dispatch = useDispatch<any>();
	const { injector } = useSelector(({ mobizReducer }) => mobizReducer);
	// const { userProfile }: IUserProfileReducer = useSelector(({ userProfileReducer }) => userProfileReducer);
	const shipmentService: IShipmentService = injector.get(IShipmentService);
	const { shipmentDetails }: { shipmentDetails: IShipmentDetails } = useShipmentDetails(shipment.id);

	const [errorFetchDocument, setErrorFetchDocument] = useState(null);

	const authService: IAuthService = injector.get(IAuthService);

	const {
		shipmentFees,
	}: {
		shipmentFees: IShipmentFees;
		shipmentFeesLoading: boolean;
		shipmentFeesError: boolean;
	} = useShipmentFees(shipment.id);

	const { t } = useTranslation('myShipments');

	const details: ShipmentTrackingEvent[] = shipmentDetails[shipment.id]?.trackingEvents;
	const documents = shipmentDetails[shipment.id]?.documents;
	const customsInfo = shipmentDetails[shipment.id]?.customsInfo;
	const getDocumentName = document => document.name?.trim() || t('unnamedFile');

	const isPostbox = shipment.deliveryServiceId === 'Postbox';
	const canDrop = shipment.canBeDroppedOffAtPostboxOrParcelPoint;

	let pTextKey;
	let firstLiKey = null;
	let secondLiKey = null;

	if (!canDrop && isPostbox) {
		// Case 1: Not droppable, but it's a Postbox service
		pTextKey = 'page-overview.payment-card-missing';
		firstLiKey = 'page-overview.bring-the-barcode';
		// secondLiKey is left as null here
	} else if (!canDrop) {
		// Case 2: Not droppable, droppable at Postoffice
		pTextKey = 'page-overview.dropOffAtPostoffice';
		secondLiKey = 'page-overview.printLabelAtPostoffice';
	} else {
		// Case 3: Droppable at Postoffice or Postbox
		pTextKey = 'page-overview.dropOffAtLocation';
		firstLiKey = 'page-overview.barcodeInfo';
		secondLiKey = 'page-overview.printLabelAtLocation';
	}

	return (
		<>
			{dropOff && shipment.direction === ShipmentDirection.Outbound ? (
				<>
					<StyledShipmentDetails.DropOffWrapperSimplified>
						<P>{t(pTextKey)}</P>
						<ul>
							{firstLiKey && <li>{t(firstLiKey)}</li>}
							{secondLiKey && <li>{t(secondLiKey)}</li>}
							<li>{t('page-overview.barcode-accessible')}</li>
						</ul>
					</StyledShipmentDetails.DropOffWrapperSimplified>
				</>
			) : shipmentDetails[shipment.id]?.loading ? (
				<StyledShipmentDetails.ShipmentAdditionalInfo>
					<RawLoader />
				</StyledShipmentDetails.ShipmentAdditionalInfo>
			) : (
				<>
					<StyledShipmentDetails.ShipmentAdditionalInfo>
						{!shipmentDetails[shipment.id]?.error && details && (
							<StyledShipmentDetails.TrackingInfo tablet={7 / 10}>
								<H4>{t('trackingHistory')}</H4>
								<ChronologicalOrderedEvents events={details} delivered={delivered} />
								{details && details.length === 0 ? <P small>{t('noTrackingEvents')}</P> : null}
							</StyledShipmentDetails.TrackingInfo>
						)}
						{!shipmentDetails[shipment.id]?.error && documents && (
							<StyledShipmentDetails.Documents tablet={3 / 10}>
								{shipmentFees[shipment.id] &&
									shipmentFees[shipment.id].length > 0 &&
									shipmentFees[shipment.id].some(fee => fee.grossAmount > 0) && (
										<StyledShipmentDetails.FeesWrapper>
											<H5>{t('paymentBreakdown')}</H5>
											{shipmentFees[shipment.id].map((fee, i) => (
												<StyledShipmentDetails.FeesList key={i}>
													<div>
														{
															fee.labels.find(
																x => x.locale === getCurrentLangPretty().toUpperCase()
															).title
														}
													</div>
													<div style={{ whiteSpace: 'nowrap' }}>
														{' ' + fee.grossAmount} kr.
													</div>
												</StyledShipmentDetails.FeesList>
											))}
											<StyledShipmentDetails.FeesList>
												<div style={{ fontWeight: theme.fonts.weight.bold }}>{t('total')}</div>
												<div style={{ fontWeight: theme.fonts.weight.bold }}>
													{shipment.totalAmount} kr.
												</div>
											</StyledShipmentDetails.FeesList>
											{!shipment.isDomestic && (
												<div style={{}}>
													{t('paymentInfoText1')}
													<a
														style={{ marginLeft: 0 }}
														href="https://www.skatturinn.is/einstaklingar/tollamal/sendingar-og-pakkar-til-landsins/reiknivel-tolla-og-gjalda/"
														target="_blank"
														rel="noopener noreferrer">
														{t('paymentInfoText2')}
													</a>
													{t('paymentInfoText3')}
												</div>
											)}
										</StyledShipmentDetails.FeesWrapper>
									)}

								<StyledShipmentDetails.DocumentsWrapper>
									<H5>{t('filesConnectedToShipment')}</H5>
									<StyledShipmentDetails.StyledErrorMessage>
										{errorFetchDocument ? (
											<div>{t('errorFetchDocument') + ' ' + errorFetchDocument}</div>
										) : (
											''
										)}
									</StyledShipmentDetails.StyledErrorMessage>
									{documents.map((document, i) => (
										<StyledShipmentDetails.FilesList key={i}>
											<div>{getDocumentName(document)}</div>
											<div>
												{/*<A
													to={shipmentService.getShipmentDocumentContentUrl(
														shipment.id,
														document.id
													)}
													target="_blank">
													{t('fetchDocument')}
												</A>*/}
												<a
													href="#file"
													onClick={async e2 => {
														e2.preventDefault(); // Prevent the default link behavior

														const getToken = async () => {
															//@ts-ignore
															const token = JSON.parse(
																window.localStorage.getItem('MobizSession')
															);
															return token.accessToken;
														};

														const fetchDocument = async (retry = false) => {
															try {
																const token = await getToken();

																const query =
																	shipmentService.getShipmentDocumentContentUrl(
																		shipment.id,
																		document.id
																	);

																// Make the request with the token included in the headers
																const response = await axios.get(query, {
																	headers: {
																		Authorization: `Bearer ${token}`,
																		Accept: 'application/json',
																		'X-Clientid': 'posturinn-web',
																		'X-Clientversion': '1.0.0',
																	},
																	responseType: 'blob', // Important for file download
																});

																if (response.status < 400) {
																	// Determine the file extension based on the Content-Type header
																	//@ts-ignore
																	const contentType =
																		response.headers['content-type'];
																	let extension = ''; // Default extension
																	const extensionMap = {
																		'image/png': '.png',
																		'image/jpeg': '.jpg', // Covers both jpg and jpeg MIME types
																		'image/jpg': '.jpg',
																		'image/gif': '.gif',
																		'image/bmp': '.bmp',
																		'application/msword': '.doc',
																		'application/vnd.openxmlformats-officedocument.wordprocessingml.document':
																			'.docx',
																		'text/plain': '.txt',
																		'application/pdf': '.pdf',
																	};

																	extension = extensionMap[contentType] || '';

																	// Handle the file download
																	//@ts-ignore
																	const url = window.URL.createObjectURL(
																		new Blob([response.data], { type: contentType })
																	);
																	//@ts-ignore
																	const link = window.document.createElement('a');
																	const filename = `${getDocumentName(document)}-${shipment.id}${extension}`; // Append the extension to the document name
																	link.href = url;
																	link.setAttribute('download', filename);
																	//@ts-ignore
																	window.document.body.appendChild(link);
																	link.click();
																	link.remove();
																	setErrorFetchDocument(null);
																}
															} catch (error) {
																if (error.response) {
																	if (error.response.status === 401 && !retry) {
																		// Refresh the token before making the request
																		await authService.refreshToken().subscribe({
																			next: () => {
																				fetchDocument(true);
																			},
																			error: () => {
																				//console.log(e);
																			},
																		});
																		// Retry the fetchDocument function
																	} else {
																		//console.error('Error fetching document:', error);
																		setErrorFetchDocument(getDocumentName(document));
																	}
																} else {
																	setErrorFetchDocument(getDocumentName(document));
																	//console.error('Error:', error);
																}
															}
														};

														// Call the fetchDocument function
														await fetchDocument();
													}}>
													{t('fetchDocument')}
												</a>
											</div>
										</StyledShipmentDetails.FilesList>
									))}
									{documents && documents.length === 0 ? <P small>{t('noDocuments')}</P> : null}
									{shipment.canAddDocuments && (
										<Button
											onClick={() =>
												dispatch(
													setActiveModal(
														ATTACH_FILES_MODAL,
														t('page-overview.customsInfoFor') + shipment.id,
														null,
														null,
														null,
														null,
														shipment.id,
														page
													)
												)
											}>
											{t('addDocuments')}
										</Button>
									)}
								</StyledShipmentDetails.DocumentsWrapper>
								{customsInfo?.customsItems && customsInfo.customsItems.length > 0 && (
									<>
										<StyledShipmentDetails.ShipmentAdditionalInfo>
											<StyledShipmentDetails.Content>
												<H5>{t('filesAdditionalInformation')}</H5>
												<>
													<StyledShipmentDetails.CustomsItemsList>
														{customsInfo.customsItems.map((item, index) => (
															<li key={index}>
																{item.description} - <strong>{item.quantity}</strong>
															</li>
														))}
														{shipment.weight && (
															<StyledShipmentDetails.CustomsWeightItem>
																{t('weight')}: {shipment.weight}
																{shipment.weightUnit}
															</StyledShipmentDetails.CustomsWeightItem>
														)}
													</StyledShipmentDetails.CustomsItemsList>
												</>
											</StyledShipmentDetails.Content>
										</StyledShipmentDetails.ShipmentAdditionalInfo>
									</>
								)}
							</StyledShipmentDetails.Documents>
						)}
					</StyledShipmentDetails.ShipmentAdditionalInfo>
				</>
			)}
		</>
	);
};

export default ShipmentDetails;
